import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AppBar, Toolbar, Typography, Container, Box, Paper, Button, Alert, Stack, CircularProgress } from '@mui/material';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import TabNavigation from './components/TabNavigation';
import BMIForm from './components/BMIForm';
import BMIResult from './components/BMIResult';
import AIInsights from './components/AIInsights';
import DashboardForm from './components/DashboardForm';
import UpgradePrompt from './components/UpgradePrompt';
import AdminPanel from './components/AdminPanel';
import ErrorBoundary from './components/ErrorBoundary';
import { fetchApi } from './hooks/useApi';
import { login, logout, handleCallback, isAuthenticated, getUserEmail, getUserGroups, SIGNED_OUT_KEY } from './auth';
import { BRAND_GRADIENT } from './theme';
import config from './config';

const AGENT_ENABLED = config.AGENT_ENABLED;
const DASHBOARD_POLL_INTERVAL_MS = 3000;
const DASHBOARD_POLL_MAX_ATTEMPTS = 40; // ~2 min; the job keeps running server-side regardless

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// Both endpoints return 403, but only quota_exceeded should show the upgrade
// prompt; an authorization failure (email != userId) must surface as an error.
const isQuotaError = (err) => err && err.status === 403 && err.message === 'quota_exceeded';

// Small centered MUI card used for the pre-content auth states (signing in,
// retry, signed-out landing) so an unauthenticated visitor never sees app content.
function AuthScreen({ children }) {
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
        <Stack spacing={2} alignItems="center">{children}</Stack>
      </Paper>
    </Container>
  );
}

/**
 * App - root component. ALL content is gated behind the Cognito front-door
 * redirect (AppSec UnauthWebEndpoint): an unauthenticated visitor is bounced to
 * the hosted UI before any SPA content renders. The calculator tab offers an AI
 * recommendation; the dashboard tab renders an agent-generated dashboard; both
 * render through the unified AIInsights surface and share one monthly free quota.
 */
function App() {
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [signedOut, setSignedOut] = useState(false);

  // Begin a Cognito login, catching a rejected initiation (insecure context,
  // sessionStorage disabled/full) so the user lands on the retry screen instead
  // of a permanent spinner. Stable identity so it can sit in the mount effect's deps.
  const beginLogin = useCallback(() => {
    setAuthLoading(true);
    login().catch(() => {
      setAuthError(true);
      setAuthLoading(false);
    });
  }, []);

  // Run the auth bootstrap exactly once (StrictMode double-invokes mount effects,
  // which would re-run handleCallback on a consumed PKCE verifier). Ref = idempotent.
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const oauthError = params.get('error');
    const cleanUrl = () => window.history.replaceState({}, '', window.location.pathname);

    // Returning from Sign Out: show a signed-out landing and do NOT auto-redirect
    // back to the hosted UI (that would bounce straight into an immediate re-login).
    if (sessionStorage.getItem(SIGNED_OUT_KEY)) {
      setSignedOut(true);
      cleanUrl();
      return;
    }

    if (code) {
      if (isAuthenticated()) {  // stale ?code (bookmark/Back) with a valid session
        cleanUrl();
        return;
      }
      setAuthLoading(true);
      handleCallback(code)
        .then(() => { setAuthenticated(true); cleanUrl(); })
        .catch(() => { setAuthenticated(false); setAuthError(true); cleanUrl(); })
        .finally(() => setAuthLoading(false));
      return;
    }

    // Cognito bounced back with an error and no code: do NOT auto-redirect (loops).
    if (oauthError) {
      if (!isAuthenticated()) setAuthError(true);
      cleanUrl();
      return;
    }

    // Force the Cognito challenge at the front door: an unauthenticated visitor is
    // redirected to the hosted UI before any content renders, so the endpoint never
    // serves the SPA anonymously (AppSec UnauthWebEndpoint — Cognito signal).
    if (!isAuthenticated()) {
      beginLogin();
    }
  }, [beginLogin]);

  // Application-level state
  const [activeTab, setActiveTab] = useState('calculator');
  const [error, setError] = useState(null);
  const [bmiResult, setBmiResult] = useState(null);

  // AI recommendation state
  const [aiRec, setAiRec] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [showAiUpgrade, setShowAiUpgrade] = useState(false);

  // Agent dashboard state
  const [dashboardUserId, setDashboardUserId] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashError, setDashError] = useState(null);
  const [showDashUpgrade, setShowDashUpgrade] = useState(false);

  // Admin tab visibility (presentation only — the backend enforces the group) and
  // the caller's own premium request status, so the UpgradePrompt can show a
  // pending state on reload without re-submitting.
  const isAdminUser = authenticated && getUserGroups().includes('admins');
  const [premiumStatus, setPremiumStatus] = useState('none');

  const refreshPremiumStatus = useCallback(async () => {
    const email = getUserEmail();
    if (!email) return;
    try {
      const tier = await fetchApi(`/user/${encodeURIComponent(email)}/tier`);
      setPremiumStatus(tier.premiumStatus || 'none');
    } catch {
      // 404 (no record yet) / transient — leave status as-is.
    }
  }, []);

  useEffect(() => {
    if (authenticated) refreshPremiumStatus();
  }, [authenticated, refreshPremiumStatus]);

  const handleBMIResult = useCallback((bmi, category, userId) => {
    setBmiResult({ bmi, category, userId });
    setError(null);
    setAiRec(null);
    setAiError(null);
    setShowAiUpgrade(false);
  }, []);

  // The recommendation is an async job (structured synthesis, same shape as the
  // dashboard): submit returns a jobId, then poll until COMPLETED/FAILED.
  const pollRecommendation = useCallback(async (jobId) => {
    for (let attempt = 0; attempt < DASHBOARD_POLL_MAX_ATTEMPTS; attempt += 1) {
      const res = await fetchApi(`/recommendation/${jobId}`);
      if (res.status === 'COMPLETED') { setAiRec({ ...res.dashboard, model: res.model }); return; }
      if (res.status === 'FAILED') { setAiError('Recommendation generation failed. Please try again.'); return; }
      await sleep(DASHBOARD_POLL_INTERVAL_MS);
    }
    setAiError('Still generating your recommendation — this is taking longer than usual. Check back shortly.');
  }, []);

  const handleGetRecommendation = useCallback(async () => {
    if (!bmiResult) return;
    setAiLoading(true);
    setAiError(null);
    setAiRec(null);
    setShowAiUpgrade(false);
    try {
      const res = await fetchApi('/recommendation', {
        method: 'POST',
        body: { userId: bmiResult.userId, bmi: bmiResult.bmi, category: bmiResult.category },
      });
      await pollRecommendation(res.jobId);
    } catch (err) {
      if (isQuotaError(err)) setShowAiUpgrade(true);
      else setAiError(err.message || 'Could not generate a recommendation.');
    } finally {
      setAiLoading(false);
    }
  }, [bmiResult, pollRecommendation]);

  const pollDashboard = useCallback(async (jobId) => {
    for (let attempt = 0; attempt < DASHBOARD_POLL_MAX_ATTEMPTS; attempt += 1) {
      const res = await fetchApi(`/dashboard/${jobId}`);
      if (res.status === 'COMPLETED') { setDashboard({ ...res.dashboard, model: res.model }); return; }
      if (res.status === 'FAILED') { setDashError('Dashboard generation failed. Please try again.'); return; }
      await sleep(DASHBOARD_POLL_INTERVAL_MS);
    }
    setDashError('Still generating your dashboard — this is taking longer than usual. Check back shortly.');
  }, []);

  const handleDashboardSubmit = useCallback(async (userId) => {
    setDashboardUserId(userId);
    setError(null);
    setDashboard(null);
    setDashError(null);
    setShowDashUpgrade(false);
    setDashLoading(true);
    try {
      const res = await fetchApi('/dashboard', { method: 'POST', body: { userId } });
      await pollDashboard(res.jobId);
    } catch (err) {
      if (isQuotaError(err)) setShowDashUpgrade(true);
      else setDashError(err.message || 'Could not start the dashboard.');
    } finally {
      setDashLoading(false);
    }
  }, [pollDashboard]);

  const handleError = useCallback((message) => setError(message), []);
  const handleTabChange = useCallback((tab) => { setActiveTab(tab); setError(null); }, []);

  const startLogin = () => { setAuthError(false); setSignedOut(false); beginLogin(); };

  // A failed sign-in must not trap the user on the spinner.
  if (authError) {
    return (
      <AuthScreen>
        <Typography>Sign-in failed.</Typography>
        <Button onClick={startLogin}>Try again</Button>
      </AuthScreen>
    );
  }

  // Deliberate sign-out landing: don't auto-redirect.
  if (signedOut) {
    return (
      <AuthScreen>
        <Typography>You've signed out.</Typography>
        <Button onClick={startLogin}>Sign in</Button>
      </AuthScreen>
    );
  }

  // Unauthenticated visitors never see app content: the mount effect has already
  // triggered the redirect. Show only the signing-in placeholder.
  if (authLoading || !authenticated) {
    return (
      <AuthScreen>
        <CircularProgress size={28} />
        <Typography color="text.secondary">Signing in…</Typography>
      </AuthScreen>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <AppBar position="static" elevation={0} sx={{ background: BRAND_GRADIENT }}>
        <Toolbar>
          <MonitorWeightIcon sx={{ mr: 1.5 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h1" sx={{ fontSize: '1.4rem', lineHeight: 1.2 }}>BMI Calculator</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Calculate your Body Mass Index and track your progress</Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>{getUserEmail()}</Typography>
            <Button color="inherit" size="small" variant="outlined" onClick={logout} sx={{ borderColor: 'rgba(255,255,255,0.6)' }}>
              Sign Out
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ pt: 4 }}>
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
          <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} showAdmin={isAdminUser} />

          {/* Calculator Tab (all tabs require sign-in via the front-door redirect) */}
          {activeTab === 'calculator' && (
            <Box id="calculator-tab">
              <BMIForm onResult={handleBMIResult} onError={handleError} />

              {bmiResult && (
                <BMIResult bmi={bmiResult.bmi} category={bmiResult.category} visible={true} />
              )}

              {AGENT_ENABLED && bmiResult && (
                <Button sx={{ mt: 2 }} onClick={handleGetRecommendation} disabled={aiLoading}>
                  {aiLoading ? 'Generating…' : 'Get AI recommendation'}
                </Button>
              )}

              {AGENT_ENABLED && (
                <ErrorBoundary message="Couldn't display the AI insights.">
                  <AIInsights
                    visible={aiLoading || !!aiRec || !!aiError}
                    loading={aiLoading}
                    error={aiError}
                    headline={aiRec && (aiRec.summary || aiRec.recommendation)}
                    trend={aiRec && aiRec.trend}
                    insights={aiRec && aiRec.insights}
                    focusAreas={aiRec && aiRec.focusAreas}
                    dataPoints={aiRec && aiRec.dataPoints}
                    disclaimer={aiRec && aiRec.disclaimer}
                    model={aiRec && aiRec.model}
                    loadingText="Generating your personalized recommendation…"
                  />
                </ErrorBoundary>
              )}

              {AGENT_ENABLED && (
                <UpgradePrompt
                  visible={showAiUpgrade}
                  userId={bmiResult && bmiResult.userId}
                  premiumStatus={premiumStatus}
                  onRequested={() => { refreshPremiumStatus(); }}
                />
              )}
            </Box>
          )}

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && AGENT_ENABLED && (
            <Box id="dashboard-tab">
              <DashboardForm onSubmit={handleDashboardSubmit} loading={dashLoading} />

              <ErrorBoundary message="Couldn't display the AI dashboard.">
                <AIInsights
                  visible={dashLoading || !!dashboard || !!dashError}
                  loading={dashLoading}
                  error={dashError}
                  headline={dashboard && dashboard.summary}
                  trend={dashboard && dashboard.trend}
                  insights={dashboard && dashboard.insights}
                  focusAreas={dashboard && dashboard.focusAreas}
                  dataPoints={dashboard && dashboard.dataPoints}
                  model={dashboard && dashboard.model}
                  loadingText="Generating your AI dashboard… this can take a few seconds."
                />
              </ErrorBoundary>

              <UpgradePrompt
                visible={showDashUpgrade}
                userId={dashboardUserId}
                premiumStatus={premiumStatus}
                onRequested={() => { refreshPremiumStatus(); }}
              />
            </Box>
          )}

          {activeTab === 'admin' && isAdminUser && (
            <Box id="admin-tab">
              <AdminPanel />
            </Box>
          )}

          {activeTab === 'dashboard' && !AGENT_ENABLED && (
            <Typography sx={{ p: 2 }}>The AI dashboard isn't enabled in this environment.</Typography>
          )}

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Paper>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
          Powered by AWS | Monitored by DevOps Agent
        </Typography>
      </Container>
    </Box>
  );
}

export default App;
